/**
 * Clasifica o ruta fata de rolul curent, pe baza `ALLOWED_ROLES` din NAV_LINKS_V2.
 *
 * Extrasa din `auth-provider-v2.validateTokenChange` (2.16, 2026-08-28) ca efectul sa poata
 * depinde de `pathname` si logica sa fie testabila in `node`, fara provideri montati.
 *
 * `'public'` NU inseamna „redirecteaza": rutele cu `ALLOWED_ROLES: []` si `layout: 'public'` sunt
 * deja tratate de `UnauthenticatedLayout`, care trimite un user logat unde ii e locul cu ACELASI
 * resolver. Daca ar decide si providerul, ar fi doi decidenti pe aceeasi navigare.
 *
 * Exceptie: rutele cu `layout: 'open'` (ex. linkul public de colaborator) au tot
 * `ALLOWED_ROLES: []`, deci clasifica la fel drept `'public'` aici — dar ocolesc INTENTIONAT
 * `UnauthenticatedLayout` (main.jsx), tocmai ca un coordonator logat sa-si poata testa propriul
 * link fara sa fie redirectat. Pentru ele, `'public'` chiar inseamna doar „vizibila fara rol",
 * nu „redirectul e altundeva rezolvat".
 */
import { matchPath } from 'react-router-dom';

import type { NavLinksType, Role } from '../../../providers/routes-provider.types';

export type PathAccess =
  | 'public' | 'allowed' | 'role-not-allowed' | 'unknown-route' | 'stage-blocked';

/** Stagiul contului care restrange accesul la o singura ruta, indiferent de `ALLOWED_ROLES`. */
export interface StageGate {
  needsSchoolRegistrationConfirmation?: boolean;
  confirmationPath: string;
}

export function classifyPathForRole(
  pathname: string,
  role: Role | undefined,
  navLinks: NavLinksType,
  stage?: StageGate
): PathAccess {
  const page = Object.values(navLinks).find((p) => matchPath(p.PATH, pathname) !== null);
  if (!page) return 'unknown-route';
  if (page.ALLOWED_ROLES.length === 0) return 'public';
  if (role && page.ALLOWED_ROLES.includes(role)) {
    // Poarta: cat timp confirmarea e ceruta, singura ruta permisa e ecranul de confirmare —
    // indiferent ca rolul ar avea voie si pe alte rute prin ALLOWED_ROLES.
    // Flag-ul `needsSchoolRegistrationConfirmation` e pasat orbeste, pentru orice rol (2.16) —
    // poarta se aplica DOAR PROFESOR-ului, ca sa nu pinuiasca ADMIN/DBEST/ELEV/LOGISTIC pe home
    // (acelasi bug ca in 2026-08-21, vezi redirect-pinuit-pe-home.md).
    if (
      role === 'PROFESOR' &&
      stage?.needsSchoolRegistrationConfirmation === true &&
      pathname !== stage.confirmationPath
    ) {
      return 'stage-blocked';
    }
    return 'allowed';
  }
  return 'role-not-allowed';
}

export function isPathAllowedForRole(
  pathname: string,
  role: Role | undefined,
  navLinks: NavLinksType,
  stage?: StageGate
): boolean {
  const access = classifyPathForRole(pathname, role, navLinks, stage);
  return access === 'public' || access === 'allowed';
}
